"use client";
import { Button, Card, Flex, Text, TextField } from "@radix-ui/themes";
import Link from "next/link";
import React, { useEffect, useMemo, useState } from "react";
import { useCountDown } from "@/common/hooks";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConfirmTsx from "@/app/features/swap/components/ConfirmTsx/ConfirmTsx";
import WaitTsx from "@/app/features/swap/components/WaitTsx/WaitTsx";
import ErrorTsx from "@/app/features/swap/components/ErrorTsx/ErrorTsx";
import {
  useAccount,
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";
import { onError } from "@/app/features/toaster/utils";
import { useAppDispatch } from "@/app/hooks";
import { notifySuccess } from "@/app/features/toaster";
import abi from "./abi.json";
import { Hex, parseEther } from "viem";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useIsMounted } from "usehooks-ts";

const nftAddress = "0x971fe8b01cb2c03cff99365cbeb7847a326f4c04";

export default function NftPage() {
  const { address } = useAccount();
  const { openConnectModal } = useConnectModal();
  const isMounted = useIsMounted()();
  const timeLeft = useCountDown(new Date("Jan 25, 2024 20:00:00 UTC"));
  const timeToMint = useCountDown(new Date("Jan 18, 2024 20:00:00 UTC"));
  const [amount, setAmount] = useState<number | "">(1);
  const dispatch = useAppDispatch();
  const {
    data: ts,
    refetch,
    isLoading,
  } = useReadContract({
    address: nftAddress,
    abi,
    functionName: "totalSupply",
  });

  const {
    isPending: isTsxPending,
    isError,
    reset,
    writeContract,
    data,
  } = useWriteContract({
    mutation: {
      onError: onError("Mint NFT", dispatch),
    },
  });
  const {
    isLoading: isTsxWaiting,
    error,
    isError: isWaitingError,
    data: transaction,
  } = useWaitForTransactionReceipt({
    hash: data,
  });
  const [curHash, setCurHash] = useState<Hex | null>(null);

  useEffect(() => {
    if (transaction && curHash !== transaction.transactionHash && address) {
      if (transaction?.status === "success") {
        refetch().then(() => {
          notifySuccess({
            hash: transaction.transactionHash,
            func: "Mint NFT",
            dispatch,
          });
        });
      } else if (transaction?.status === "reverted" || isWaitingError) {
        onError("Mint NFT", dispatch);
      }
      setCurHash(transaction.transactionHash);
    }
  }, [address, curHash, dispatch, isWaitingError, refetch, transaction]);

  const amountLeft = useMemo(() => 690n - ((ts as bigint) || 0n), [ts]);
  const maxAmount = useMemo(
    () => (amountLeft > 5n ? 5n : amountLeft),
    [amountLeft],
  );

  return (
    <>
      {isTsxPending && <ConfirmTsx reset={reset} />}
      {isTsxWaiting && <WaitTsx isMinting hash={data} />}
      {isWaitingError && (
        <ErrorTsx
          isMinting
          hash={data}
          error={error}
          reset={reset}
          retry={() =>
            writeContract?.({
              abi,
              address: nftAddress,
              functionName: "mint",
              args: [amount],
              value: parseEther((0.05 * (amount || 0)).toFixed(2)),
            })
          }
        />
      )}
      <Card
        size="2"
        className="relative z-20 m-auto max-w-[380px] md:max-w-3xl"
        data-radius="large"
      >
        <Flex direction="column" gap="3">
          <Flex align="center" justify="between" gap="6" px="2">
            <Flex align="center" gap="3">
              <Text
                className="font-semibold text-grayA-9 hover:text-inherit"
                asChild
              >
                <Link href="/">Swap</Link>
              </Text>
              <Text className="font-semibold">NFTs</Text>
            </Flex>
          </Flex>

          <Flex px="2" gap="6" className="flex-col md:flex-row">
            <Flex className="flex-1">
              <img src="/nft.gif" alt="nft" className="max-w-full" />
            </Flex>
            <Flex direction="column" gap="6" className="flex-1">
              <Flex direction="column" gap="3">
                <Text className="font-semibold" size="7">
                  Froge NFT mint
                </Text>
                <Flex direction="column" gap="1">
                  <Text>🐸 Explore the unique world of Froge NFTs!</Text>
                  <Text>690 randomised digital frogs.</Text>
                  <Text>Own a piece of rarity and digital charm! 🌈</Text>
                  <Flex gap="4" mt="2">
                    {!timeToMint && (
                      <Text
                        asChild
                        color="green"
                        className="flex items-center gap-1"
                      >
                        <Link
                          href={`https://opensea.io/assets/ethereum/${nftAddress}`}
                          target="_blank"
                        >
                          OpenSea
                          <FontAwesomeIcon size="xs" icon={faExternalLink} />
                        </Link>
                      </Text>
                    )}
                    <Text
                      asChild
                      color="green"
                      className="flex items-center gap-1"
                    >
                      <Link
                        href={`https://docs.frogeswap.vip/frogenft-collection`}
                        target="_blank"
                      >
                        Docs
                        <FontAwesomeIcon size="xs" icon={faExternalLink} />
                      </Link>
                    </Text>
                  </Flex>
                </Flex>
                {(timeToMint || timeLeft) && (
                  <Flex direction="column" gap="2" mt="3">
                    <Text>{timeToMint ? "Mint" : "Reveal"} countdown:</Text>
                    <div className="flex gap-2 text-lg">
                      <div className="flex flex-col">
                        <span className="text-sm text-grayA-11">Days</span>
                        <span>{timeToMint?.days || timeLeft?.days}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-grayA-11">Hours</span>
                        <span>{timeToMint?.hours || timeLeft?.hours}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-grayA-11">Minutes</span>
                        <span>{timeToMint?.minutes || timeLeft?.minutes}</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-grayA-11">Seconds</span>
                        <span>{timeToMint?.seconds || timeLeft?.seconds}</span>
                      </div>
                    </div>
                  </Flex>
                )}
                <Flex gap="2" mt="3">
                  <Flex gap="1" className="flex-1">
                    <Text>Price</Text>
                    <Text color="gray">
                      {(0.05 * (amount || 1)).toFixed(2)} ETH
                    </Text>
                  </Flex>
                  <Flex gap="1" className="flex-1" align="center">
                    <Text>Froges left</Text>
                    {isLoading ? (
                      <span className="h-[16px] w-[61px] animate-pulse rounded bg-gray-5"></span>
                    ) : (
                      <Text color="gray">{amountLeft.toString()}/690</Text>
                    )}
                  </Flex>
                </Flex>
              </Flex>
              <Flex gap="4">
                <div className="min-w-[70px] max-w-[70px]">
                  <TextField.Input
                    size="3"
                    placeholder="Amount"
                    type="number"
                    min={1}
                    max={Number(maxAmount)}
                    value={amount}
                    onChange={(e) => {
                      const newValue = e.target.valueAsNumber;

                      if (isNaN(newValue)) {
                        setAmount("");
                        return;
                      }

                      if (newValue >= 1 && newValue <= Number(maxAmount)) {
                        setAmount(newValue);
                      }
                    }}
                  />
                </div>
                {isMounted && address ? (
                  <Button
                    size="3"
                    className="flex-1"
                    disabled={
                      !writeContract ||
                      amountLeft < 1n ||
                      !!timeToMint ||
                      isError ||
                      !amount ||
                      amount <= 0
                    }
                    onClick={() =>
                      writeContract?.({
                        abi,
                        address: nftAddress,
                        functionName: "mint",
                        args: [amount],
                        value: parseEther((0.05 * (amount || 0)).toFixed(2)),
                      })
                    }
                  >
                    {isError
                      ? "Insufficient ETH"
                      : amountLeft < 1n
                        ? "Sold out"
                        : "Mint"}
                  </Button>
                ) : (
                  <Button
                    size="3"
                    className="flex-1"
                    onClick={openConnectModal}
                  >
                    Connect Wallet
                  </Button>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </>
  );
}
